import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import { Input, Option,Select } from "@admin/components/atoms/Input";
import { Label } from "@admin/components/atoms/Input/Label";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import {
  CheckboxStateValue,
  MultiCheckbox,
} from "@admin/components/atoms/MultiStateCheckbox/MultiCheckbox";
import {
  addPestsRevisionControl,
  answerMulticheckValues,
  FoodControlPointInstance,
  FoodManagementControls,
  InstanceStatus,
  parseRemarkAndCorrectiveActions,
  PestsRevision,
  updatePestsRevisionControl,
} from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation,useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface AddPestsRevisionControlModalProps extends ModalProps {
  foodControlPointInstance: FoodControlPointInstance;
  type: FoodManagementControls.pestsRevision;
  initialData?: PestsRevision;
}

export function AddPestsRevisionControlModal({
  foodControlPointInstance,
  type,
  initialData,
  ...props
}: AddPestsRevisionControlModalProps) {
  const { t } = useTranslation();
  const [isCorrectiveActionNeeded, setIsCorrectiveActionNeeded] =
    useState<boolean>(false);
  const [isRemarksNeeded, setIsRemarksNeeded] = useState<boolean>(false);
  const [note, setNote] = useState<string[]>([]);
  const numberOfCorrectiveActionSelected = useRef(0);
  const [checkBoxStates, setCheckBoxStates] = useState<CheckboxStateValue[]>(
    [],
  );
  const addPestControlSchema = z.object({
    remarks: z
      .string()
      .optional()
      .refine((e) => !isRemarksNeeded, {
        message: t("food.Remarks are mandatory"),
      }),
    correctiveAction: z
      .boolean()
      .optional()
      .refine(
        (e) =>
          isCorrectiveActionNeeded
            ? !!numberOfCorrectiveActionSelected.current
            : true,
        {
          message: t("food.At leat one corrective action needed"),
        },
      ),
    contact: z.string().min(1, t("food.Contact Person Required")),
    fixedAgreement: z.string().min(1),
  });
  type AddPestControlData = z.infer<typeof addPestControlSchema>;
  function processValidationNumbers(checkBoxStates: CheckboxStateValue[]) {
    const valueToNumberMap: { [key: string]: number } = {
      yes: 1,
      partial: 2,
      no: 3,
    };

    const result: {
      validateNet: number;
      validateDoorAndWindow: number;
      validateSewers: number;
      validateWallStorage: number;
      validatePestEntrance: number;
    } = {
      validateNet: 1,
      validateDoorAndWindow: 1,
      validateSewers: 1,
      validateWallStorage: 1,
      validatePestEntrance: 1,
    };

    checkBoxStates.forEach((state) => {
      const numberValue = valueToNumberMap[state.value as string];
      switch (state.stateName) {
        case "validateNet":
          result.validateNet = numberValue;
          break;
        case "validateDoorAndWindow":
          result.validateDoorAndWindow = numberValue;
          break;
        case "validateSewers":
          result.validateSewers = numberValue;
          break;
        case "validateWallStorage":
          result.validateWallStorage = numberValue;
          break;
        case "validatePestEntrance":
          result.validatePestEntrance = numberValue;
          break;
        default:
          break;
      }
    });

    return result;
  }

  const agreementPestOptions: Option[] = [
    { value: 1, label: t("food.Pre Arranged") },
    { value: 2, label: t("food.Not Pre Arranged") },
  ];

  const checkBoxTexts: string[] = [
    t("food.Nets Control"),
    t("food.Window and Doors"),
    t("food.Sewer Covers"),
    t("food.Vegetation"),
    t("food.Pest Connection"),
  ];

  const queryClient = useQueryClient();
  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm<AddPestControlData>({
    resolver: zodResolver(addPestControlSchema),
  });

  const checkBoxData: CheckboxStateValue[] = useMemo(
    () => [
      {
        value: initialData
          ? answerMulticheckValues(initialData.validateNet)
          : "yes",
        stateName: "validateNet",
      },
      {
        value: initialData
          ? answerMulticheckValues(initialData.validateDoorAndWindow)
          : "yes",
        stateName: "validateDoorAndWindow",
      },
      {
        value: initialData
          ? answerMulticheckValues(initialData.validateSewers)
          : "yes",
        stateName: "validateSewers",
      },
      {
        value: initialData
          ? answerMulticheckValues(initialData.validateWallStorage)
          : "yes",
        stateName: "validateWallStorage",
      },
      {
        value: initialData
          ? answerMulticheckValues(initialData.validatePestEntrange)
          : "yes",
        stateName: "validatePestEntrance",
      },
    ],
    [initialData],
  );

  const updateMutation = useMutation({
    mutationFn: updatePestsRevisionControl,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPoints",
          foodControlPointInstance.companyLocationId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPointInstances",
          foodControlPointInstance?.companyLocationId,
        ],
      });
      reset();
      setIsCorrectiveActionNeeded(false);
      setIsRemarksNeeded(false);
      setNote([]);
      numberOfCorrectiveActionSelected.current = 0;
      setCheckBoxStates(checkBoxData);
      props.onClose?.();
      toast.success(t("food.Pest Control Has Been Added"), {
        duration: 4000,
      });
    },
    onError: () => {
      toast.error(t("food.An error occurred while adding Pest Control"));
    },
  });

  const mutation = useMutation({
    mutationFn: addPestsRevisionControl,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPoints",
          foodControlPointInstance.companyLocationId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPointInstances",
          foodControlPointInstance?.companyLocationId,
        ],
      });
      reset();
      setIsCorrectiveActionNeeded(false);
      setIsRemarksNeeded(false);
      setNote([]);
      numberOfCorrectiveActionSelected.current = 0;
      setCheckBoxStates(checkBoxData);
      props.onClose?.();
      toast.success(t("food.Pest Control Has Been Added"), {
        duration: 4000,
      });
    },
    onError: () => {
      toast.error(t("food.An error occurred while adding Pest Control"));
    },
  });

  const noteTexts = {
    note1: t("food.Resolved Internally"),
    note2: t("food.Contact Pest"),
    note3: t("food.Food Safety Authority"),
    note4: t("food.Other"),
  };

  const editNote = (e: React.ChangeEvent<HTMLInputElement>) => {
    const noteText = noteTexts[e.target.name as keyof typeof noteTexts];
    const targetName = e.target.name;
    const isChecked = e.target.checked;
    isChecked
      ? ++numberOfCorrectiveActionSelected.current
      : --numberOfCorrectiveActionSelected.current;
    isChecked
      ? setNote((prev) => [...prev, noteText])
      : setNote((prev) => prev.filter((note) => note !== noteText));
    if (targetName === "note4" || targetName === "note1") {
      setIsRemarksNeeded(isChecked);
    }
  };
  const handleAction = (e: CheckboxStateValue) => {
    setCheckBoxStates((prevState) => {
      const newState = prevState.map((state) =>
        state.stateName === e.stateName ? { ...state, value: e.value } : state,
      );

      const isCorrectiveNeeded = newState.some(
        (state) => state.value === "partial" || state.value === "no",
      );

      setIsCorrectiveActionNeeded(isCorrectiveNeeded);

      return newState;
    });
  };

  useEffect(() => {
    if (checkBoxStates.length === 0) {
      setCheckBoxStates(checkBoxData);
    }

    if (initialData) {
      const { remark, reason } = parseRemarkAndCorrectiveActions(
        initialData.note,
      );

      setNote(reason);
      setIsCorrectiveActionNeeded(
        initialData.status === InstanceStatus.completedWithErrors,
      );
      setIsRemarksNeeded(remark.includes(noteTexts.note1 || noteTexts.note4));

      reset({
        remarks: remark,
        contact: initialData.contact,
        fixedAgreement: initialData.fixedAgreement,
      });
    }
  }, [
    checkBoxData,
    checkBoxStates.length,
    initialData,
    noteTexts.note1,
    noteTexts.note4,
    reset,
  ]);

  const onSubmit = (data: AddPestControlData) => {
    const validationNumbers = processValidationNumbers(checkBoxStates);
    const isCompeletedWithErrors = isCorrectiveActionNeeded;

    const payload = {
      companyId: foodControlPointInstance.companyId,
      companyLocationId: foodControlPointInstance.companyLocationId,
      reason: JSON.stringify(note),
      remark: data.remarks,
      status: isCompeletedWithErrors
        ? InstanceStatus.completedWithErrors
        : InstanceStatus.completed,
      fixedAgreement:
        agreementPestOptions.find(
          (agr) => agr.value === Number(data.fixedAgreement),
        )?.label ?? "",
      validateNet: validationNumbers.validateNet,
      validateDoorAndWindow: validationNumbers.validateDoorAndWindow,
      validateSewers: validationNumbers.validateSewers,
      validateWallStorage: validationNumbers.validateWallStorage,
      validatePestEntrange: validationNumbers.validatePestEntrance,
      contact: data.contact,
      controlPointInstanceId: foodControlPointInstance.id,
      foodControlPointInstance,
    };

    if (initialData) {
      updateMutation.mutate(payload);
      return;
    }

    mutation.mutate(payload);
  };

  return (
    <Modal
      actionButtons={
        <>
          <Button variant="secondary" onClick={props?.onClose}>
            {t("common.Discard")}
          </Button>
          <Button loading={mutation.isPending} onClick={handleSubmit(onSubmit)}>
            {t("food.Submit Entry")}
          </Button>
        </>
      }
      {...props}
      title={t("food.Pest Inspection")}
    >
      <form>
        <div className="flex flex-col gap-y-6">
          {checkBoxTexts.map((text, index) => {
            return (
              <div
                key={checkBoxData[index].stateName + "_" + index}
                className="flex flex-row items-center gap-x-3"
              >
                <label className="form-label !font-normal">{text}</label>
                <div className="flex flex-col gap-2">
                  <MultiCheckbox
                    stateName={checkBoxData[index].stateName}
                    onChange={(e) => handleAction(e)}
                    value={
                      checkBoxStates.find(
                        (state) =>
                          state.stateName === checkBoxData[index].stateName,
                      )?.value
                    }
                  />
                </div>
              </div>
            );
          })}
        </div>
        <div className="border-b border-b-gray-200 my-2.5"></div>
        <div>
          <Label title={t("food.Exterminator")}>
            <Select
              options={agreementPestOptions}
              placeholder={t("common.Select an option")}
              {...register("fixedAgreement")}
              defaultValue={agreementPestOptions[0].value}
            />
          </Label>
          <ErrorMessage message={errors?.fixedAgreement?.message} />
        </div>
        <div>
          <Label title={t("food.Contact Infestation")}>
            <Input {...register("contact")} />
          </Label>
          <ErrorMessage message={errors?.contact?.message} />
        </div>

        {isCorrectiveActionNeeded && (
          <>
            <div className="border-b border-b-gray-200 my-2.5"></div>
            <Label required title={t("food.Corrective Action")}>
              <div className="flex flex-col w-full gap-2 ">
                <div className="flex flex-row gap-4 items-center">
                  <input
                    name="note1"
                    className="checkbox checkbox-md"
                    type="checkbox"
                    defaultChecked={note.includes(noteTexts.note1)}
                    onChange={(e) => editNote(e)}
                  />
                  {noteTexts.note1}
                </div>
                <div className="flex flex-row gap-4 items-center">
                  <input
                    onChange={(e) => editNote(e)}
                    className="checkbox checkbox-md"
                    defaultChecked={note.includes(noteTexts.note2)}
                    type="checkbox"
                    name="note2"
                  />
                  {noteTexts.note2}
                </div>
                <div className="flex flex-row gap-4 items-center">
                  <input
                    name="note3"
                    className="checkbox checkbox-md"
                    defaultChecked={note.includes(noteTexts.note3)}
                    type="checkbox"
                    onChange={(e) => editNote(e)}
                  />
                  {noteTexts.note3}
                </div>
                <div className="flex flex-row gap-4 items-center">
                  <input
                    name="note4"
                    className="checkbox checkbox-md"
                    defaultChecked={note.includes(noteTexts.note4)}
                    type="checkbox"
                    onChange={(e) => editNote(e)}
                  />
                  {noteTexts.note4}
                </div>
                <ErrorMessage message={errors?.correctiveAction?.message} />
              </div>
            </Label>
          </>
        )}
        <div className="border-b border-b-gray-200 my-2.5"></div>
        <Label title={t("food.Remarks")}>
          <div className="flex flex-col items-end w-full gap-2 ">
            <Input
              {...register("remarks")}
              showRequired={isRemarksNeeded}
              placeholder="-"
              onChange={(e) => {
                setIsRemarksNeeded(!e.target.value);
              }}
            />
            <ErrorMessage message={errors?.remarks?.message} />
          </div>
        </Label>
      </form>
    </Modal>
  );
}
